/*
 * BJD global video sound control — 2026-07-16 (Cale, dev theme).
 *
 * Problem it solves: the unmute affordance was inconsistent across the site
 * (PDP hero had a bespoke top-right icon button; reels + galleries relied on
 * tiny native controls), and it was icon-only, so a first-time visitor couldn't
 * tell how to turn sound on.
 *
 * This injects ONE standardized, labeled "Tap for sound" pill onto every muted
 * content video, always in the same place (top-right), and enforces
 * one-audio-at-a-time. Single source of truth => same control everywhere.
 *
 * Scope: only muted CONTENT videos are enhanced — a video is a target if it is
 * muted AND (has native `controls` OR is the PDP hero video). Decorative /
 * background videos (muted, no controls) are left alone. The customer's own
 * delivered video (bjd-ondemand-ready) is NOT muted, so it is skipped.
 */
(function () {
  'use strict';

  var PILL_CLASS = 'bjd-sound-pill';
  var DONE_ATTR = 'data-bjd-sound-ready';
  var ICON_MUTED = '🔇';   // 🔇
  var ICON_LOUD = '🔊';     // 🔊
  var registry = [];

  function injectStyles() {
    if (document.getElementById('bjd-sound-styles')) return;
    var css =
      '.' + PILL_CLASS + '{position:absolute;top:10px;right:10px;z-index:6;display:inline-flex;' +
      'align-items:center;gap:7px;padding:9px 13px;border:0;border-radius:999px;' +
      'background:rgba(10,10,10,.66);color:#fff;font-family:"Bebas Neue","Anton",system-ui,-apple-system,sans-serif;' +
      'font-size:13px;letter-spacing:.12em;text-transform:uppercase;line-height:1;cursor:pointer;' +
      '-webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px);box-shadow:0 2px 10px rgba(0,0,0,.35);' +
      'transition:opacity .2s ease,transform .08s ease;-webkit-tap-highlight-color:transparent;}' +
      '.' + PILL_CLASS + ':hover{transform:translateY(-1px);}' +
      '.' + PILL_CLASS + ':focus-visible{outline:2px solid #FF0A7A;outline-offset:2px;}' +
      '.' + PILL_CLASS + ' .bjd-sound-ico{font-size:15px;line-height:1;}' +
      '.' + PILL_CLASS + '[data-state="unmuted"] .bjd-sound-txt{display:none;}' +
      '.' + PILL_CLASS + '[data-state="unmuted"]{background:rgba(255,10,122,.85);}' +
      '.' + PILL_CLASS + '[data-state="muted"]{animation:bjdSoundPulse 2.4s ease-in-out infinite;}' +
      '@keyframes bjdSoundPulse{0%,100%{box-shadow:0 2px 10px rgba(0,0,0,.35),0 0 0 0 rgba(255,10,122,.5);}' +
      '50%{box-shadow:0 2px 10px rgba(0,0,0,.35),0 0 0 8px rgba(255,10,122,0);}}' +
      '@media (prefers-reduced-motion:reduce){.' + PILL_CLASS + '[data-state="muted"]{animation:none;}}';
    var style = document.createElement('style');
    style.id = 'bjd-sound-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  function isTarget(video) {
    var muted = video.muted || video.hasAttribute('muted');
    if (!muted) return false;
    return video.hasAttribute('controls') || video.classList.contains('bjd-hero-video');
  }

  function paint(video, btn, muted) {
    video.muted = muted;
    if (!muted) video.removeAttribute('muted');
    btn.setAttribute('data-state', muted ? 'muted' : 'unmuted');
    btn.setAttribute('aria-pressed', muted ? 'false' : 'true');
    btn.setAttribute('aria-label', muted ? 'Tap for sound' : 'Mute');
    var ico = btn.querySelector('.bjd-sound-ico');
    if (ico) ico.textContent = muted ? ICON_MUTED : ICON_LOUD;
  }

  function toggle(video, btn) {
    if (video.muted) {
      // Unmuting this one: silence every other enhanced video first.
      for (var i = 0; i < registry.length; i++) {
        if (registry[i].video !== video) paint(registry[i].video, registry[i].btn, true);
      }
      paint(video, btn, false);
      if (video.paused) {
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }
    } else {
      paint(video, btn, true);
    }
  }

  function enhance(video) {
    if (video.getAttribute(DONE_ATTR)) return;
    if (!isTarget(video)) return;
    var parent = video.parentElement;
    if (!parent) return;
    video.setAttribute(DONE_ATTR, '1');

    if (window.getComputedStyle(parent).position === 'static') {
      parent.style.position = 'relative';
    }

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = PILL_CLASS;
    btn.innerHTML =
      '<span class="bjd-sound-ico" aria-hidden="true">' + ICON_MUTED + '</span>' +
      '<span class="bjd-sound-txt">Tap for sound</span>';
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      toggle(video, btn);
    });
    parent.appendChild(btn);
    registry.push({ video: video, btn: btn });
    paint(video, btn, true);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var vids = scope.querySelectorAll('video');
    for (var i = 0; i < vids.length; i++) enhance(vids[i]);
  }

  function init() {
    injectStyles();
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-scan when a section is (re)loaded so the pill persists.
  document.addEventListener('shopify:section:load', function (e) {
    injectStyles();
    scan(e.target || document);
  });
})();
